import { type ReactNode, type JSX } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { useThemeContext } from '../lib/theme-context';
import { useCardAccess } from '../lib/card-access-context';
import { matchCardPath } from '../lib/card-access';
import { WEB_NAV_CARD_PATHS } from '../config/nav-groups';
import { MfaChallengeGate } from './MfaChallengeGate';

interface ProtectedRouteProps {
  readonly children: ReactNode;
}

export function ProtectedRoute({ children }: ProtectedRouteProps): JSX.Element | null {
  const { user, loading, role, companyId } = useAuth();
  const { isThemeLoaded } = useThemeContext();

  // Still resolving auth session from persisted storage
  if (loading) return null;

  // Auth resolved: no user → redirect to login immediately
  if (!user) return <Navigate to="/login" replace />;

  // A superadmin has no company of their own — they act on one they select.
  // Until they have, every tenant screen would hang on a null companyId (and
  // every API call would 428), so send them to the picker.
  if (role === 'superadmin' && !companyId) return <Navigate to="/select-company" replace />;

  // User authenticated: wait for theme before rendering children
  if (!isThemeLoaded) return null;

  // Second factor may still be owed — see MfaChallengeGate. The card check sits
  // inside it so the code prompt always shows first.
  return (
    <MfaChallengeGate>
      <CardRouteGuard>{children}</CardRouteGuard>
    </MfaChallengeGate>
  );
}

// Card pages are limited by the user's job title. Opening one the title doesn't
// include — even by typing its address, e.g. /ml-hours — lands on the dashboard.
// Pages that aren't cards (dashboard, to-do list) are open to everyone.
function CardRouteGuard({ children }: ProtectedRouteProps): JSX.Element | null {
  const location = useLocation();
  const { loading, canView } = useCardAccess();

  const cardPath = matchCardPath(location.pathname, WEB_NAV_CARD_PATHS);
  if (cardPath === null) return <>{children}</>;
  if (loading) return null;
  if (!canView(cardPath)) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}
